/**
 * The course, as a map.
 *
 * The first version of this was fifty anonymous dots and a "12 / 50". That
 * counts, which is not the same as tracking: it tells you how far along you are
 * and nothing about where that is, what is coming, or what you did last week.
 *
 * Three things it now says instead.
 *
 * 1. WHERE YOU ARE, in blocks with names. "Lesson 31" means nothing; "Dativ,
 *    first of four" is a position in the language. The block numbers come from
 *    the curriculum and travel with each lesson; only the names live in the
 *    client.
 *
 * 2. WHEN IT ENDS. A finite curriculum is the whole premise of this app and the
 *    only thing it can show that an endless one cannot — so the remaining count
 *    is given in days, which is the unit the promise was made in.
 *
 * 3. WHAT YOU ALREADY DID. `first_done_date` has been recorded since the first
 *    migration and was never displayed. A finished lesson is now a link back to
 *    its rule, which is the thing you actually want three weeks later when you
 *    have forgotten whether it was `dem` or `den`.
 */
import { Check, ChevronRight } from 'lucide-react';
import { Link } from 'react-router';

import { cn } from '@/lib/utils';

import { readingOrder, type GrammarCourse } from './types';

/**
 * The state both grammar routes return. Identical in shape for either course —
 * only the language inside `title` differs, which `readingOrder` handles.
 */
export type GrammarState = {
  done_today: boolean;
  lessons_done: number[];
  highest_done: number;
  last_authored: number;
  next_lesson: { n: number; title: Record<string, string> } | null;
  outline: {
    n: number;
    slug: string;
    block: number;
    authored: boolean;
    title: Record<string, string> | null;
    /** YYYY-MM-DD the lesson was first completed, or null. */
    done_date: string | null;
  }[];
};

type OutlineEntry = GrammarState['outline'][number];

export function GrammarProgress({
  state,
  appId,
  course,
  primaryLang,
}: {
  state: GrammarState;
  appId: string;
  course: GrammarCourse;
  primaryLang?: string;
}) {
  const done = new Set(state.lessons_done);
  const total = state.outline.length;
  const remaining = total - done.size;
  const next = state.next_lesson?.n ?? null;

  const blocks = new Map<number, OutlineEntry[]>();
  for (const lesson of state.outline) {
    blocks.set(lesson.block, [...(blocks.get(lesson.block) ?? []), lesson]);
  }

  return (
    <section className="flex flex-col gap-5">
      <Summary done={done.size} total={total} remaining={remaining} />

      <div className="flex flex-col gap-4">
        {[...blocks.entries()].map(([block, lessons]) => (
          <Block
            key={block}
            appId={appId}
            course={course}
            primaryLang={primaryLang}
            name={course.blockName(block)}
            lessons={lessons}
            done={done}
            next={next}
          />
        ))}
      </div>
    </section>
  );
}

function Summary({ done, total, remaining }: { done: number; total: number; remaining: number }) {
  // One lesson a day is the whole design, so days remaining is just the count.
  // Whole weeks only: "5.4 weeks" is a precision nobody has about their own
  // study habits, and false precision reads as a bug in the estimate.
  const weeks = Math.round(remaining / 7);

  return (
    <div className="border-border flex flex-col gap-1 border-b pb-4">
      <div className="flex items-baseline gap-2">
        <span className="font-display text-3xl font-semibold tabular-nums">{done}</span>
        <span className="text-muted-foreground text-sm">of {total} lessons</span>
      </div>
      <p className="text-muted-foreground text-sm">
        {remaining === 0 ? (
          <>The curriculum is finished. Every session from here is review.</>
        ) : (
          <>
            {remaining} to go —{' '}
            {remaining >= 7 ? <>about {weeks} weeks at one a day.</> : <>under a week to go.</>}
          </>
        )}
      </p>
    </div>
  );
}

function Block({
  appId,
  course,
  primaryLang,
  name,
  lessons,
  done,
  next,
}: {
  appId: string;
  course: GrammarCourse;
  primaryLang?: string;
  name: string;
  lessons: OutlineEntry[];
  done: Set<number>;
  next: number | null;
}) {
  const doneHere = lessons.filter((lesson) => done.has(lesson.n)).length;
  const complete = doneHere === lessons.length;
  const current = lessons.some((lesson) => lesson.n === next);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline gap-2">
        <h3
          className={cn(
            'text-sm font-medium',
            complete
              ? 'text-muted-foreground'
              : current
                ? 'text-foreground'
                : 'text-muted-foreground',
          )}
        >
          {name}
        </h3>
        {complete && <Check className="text-success size-3.5 shrink-0" aria-hidden />}
        <span className="text-muted-foreground ml-auto text-xs tabular-nums">
          {doneHere}/{lessons.length}
        </span>
      </div>

      <ul className="flex flex-col">
        {lessons.map((lesson) => (
          <LessonRow
            key={lesson.n}
            appId={appId}
            course={course}
            primaryLang={primaryLang}
            lesson={lesson}
            done={done.has(lesson.n)}
            isNext={lesson.n === next}
          />
        ))}
      </ul>
    </div>
  );
}

function LessonRow({
  appId,
  course,
  primaryLang,
  lesson,
  done,
  isNext,
}: {
  appId: string;
  course: GrammarCourse;
  primaryLang?: string;
  lesson: OutlineEntry;
  done: boolean;
  isNext: boolean;
}) {
  // Was hardcoded to `title.en`, which showed a Brazilian learner of German
  // her third-best language in her own progress list. Ordered by her primary
  // now, and by whatever the lesson actually carries if neither is present.
  const label = readingOrder(lesson.title, course, primaryLang)[0] ?? lesson.slug;

  const body = (
    <>
      <span
        className={cn(
          'w-6 shrink-0 text-xs tabular-nums',
          done ? 'text-primary' : 'text-muted-foreground',
        )}
      >
        {lesson.n}
      </span>
      <span className={cn('truncate', done ? 'text-foreground' : 'text-muted-foreground')}>
        {label}
      </span>
      {isNext && (
        <span className="bg-primary/10 text-primary ml-2 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium">
          next
        </span>
      )}
      {lesson.done_date && (
        <span className="text-muted-foreground ml-auto shrink-0 text-xs tabular-nums">
          {shortDate(lesson.done_date)}
        </span>
      )}
      {done && <ChevronRight className="text-muted-foreground size-3.5 shrink-0" aria-hidden />}
    </>
  );

  // Only a finished lesson is a link. An unread rule is not a thing to browse
  // to — reaching it is the point, and a curriculum you can skim ahead in is
  // just a reference book with a progress bar on it.
  if (!done) {
    return (
      <li
        className={cn(
          'flex items-center gap-2 py-1 text-sm',
          !lesson.authored && 'opacity-40',
          isNext && 'font-medium',
        )}
      >
        {body}
      </li>
    );
  }

  return (
    <li>
      <Link
        to={`/apps/${appId}/lessons/${lesson.n}`}
        className="hover:bg-muted/60 focus-visible:ring-ring -mx-2 flex items-center gap-2 rounded px-2 py-1 text-sm focus-visible:ring-2 focus-visible:outline-none"
      >
        {body}
      </Link>
    </li>
  );
}

/** "12 Aug" — the year is noise inside a seven-week course. */
function shortDate(iso: string): string {
  const [, month, day] = iso.split('-').map(Number);
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${day} ${months[month - 1] ?? ''}`.trim();
}
